import { GridColDef } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { baseURL } from "../../../config/api";
import { IconButton, Menu, MenuItem, Stack, Tooltip } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { CustomDataGrid } from "../../../components/CustomDataGrid";
import moment from "moment";
import { getStatusColor } from "../../../utils/functionHelper";

const filterItems = [
    {
        id: 0,
        label: "PO No.",
    },
    {
        id: 1,
        label: "Vendor Name",
    },
    {
        id: 2,
        label: "Status",
    }
]

export const PurchaseOrders = () => {
    const navigate = useNavigate();
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });
    const [searchTableData, setSearchTableData] = useState<any[]>([]);
    const [searchValue, setSearchValue] = useState<string>("");
    const [isFirstTime, setIsFirstTime] = useState<boolean>(true);
    const [selectedFilter, setSelectedFilter] = useState<string>(filterItems[0].label);
    const [filterValue, setFilterValue] = useState(0);
    const [totalLength, setTotalLength] = useState<number>(0);
    const [loading, setLoading] = useState(true);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const columns: GridColDef[] = [
        {
            field: "id",
            headerName: "PO Number",
            flex: 1,
            editable: false,
            sortable: false,
            renderCell: (params) => {
                return (
                    <div className="flex items-center gap-2">
                        <Link
                            to="/admin/purchase-orders/edit-purchase-order"
                            state={params?.id}
                            className="underline text-[#0000ff]"
                        >
                            {params?.value}
                        </Link>

                        <Tooltip title="Export PDF">
                            <IconButton size="small">
                                <Link
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    to={`/admin/PurchaseOrder/${params.row.id}`}
                                    className="flex items-center"
                                >
                                    <PictureAsPdfIcon fontSize="small" color="error" />
                                </Link>
                            </IconButton>
                        </Tooltip>

                    </div>
                );
            },
        },
        {
            field: "reference",
            headerName: "Reference",
            flex: 1,
            editable: false,
            sortable: false,
        },
        {
            field: "totalCost",
            headerName: "Total Cost",
            flex: 1,
            editable: false,
            sortable: false,
        },
        {
            field: "vendorName",
            headerName: "Vendor Name",
            flex: 1,
            editable: false,
            sortable: false,
        },
        {
            field: "totalCostReceived",
            headerName: "Total Cost Received",
            flex: 1,
            editable: false,
            sortable: false,
        },
        {
            field: "dueDate",
            headerName: "DueDate",
            flex: 1,
            editable: false,
            sortable: false,
        },
        {
            field: "shipVia",
            headerName: "Ship Via",
            editable: false,
            sortable: false,
            flex: 1,
        },
        {
            field: "instructions",
            headerName: "Instructions",
            flex: 1,
            editable: false,
            sortable: false,
        },
        {
            field: "status",
            headerName: "Status",
            flex: 1,
            editable: false,
            sortable: false,
            renderCell: (params) => {
                const color = getStatusColor(params.value);
                return (
                    <div className="flex items-end h-full">
                        <span className={`inline-flex text-xs px-[10px] py-[2px] rounded-xl font-semibold ${color.colorBg} ${color.colorText}`}>
                            {params.value}
                        </span>
                    </div>
                );
            }
        },
    ];

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    useEffect(() => {
        let timeOutId: NodeJS.Timeout;

        if (searchValue !== "") {
            timeOutId = setTimeout(() => {
                const newPaginationModel = {
                    page: 0,
                    pageSize: paginationModel?.pageSize,
                };

                setPaginationModel(newPaginationModel);
            }, 1000);
        }

        return () => clearTimeout(timeOutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);

    useEffect(() => {
        if (searchValue !== "") {
            filterApiCall(paginationModel);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel])

    const filterApiCall = (newPaginationModel: any) => {
        setLoading(true);
        let filterData: any[] = [];
        setLoading(true);

        switch (filterValue) {
            case 0:
                fetch(`${baseURL}purchaseOrders/GetSearchPurchaseOrdersByNumber/${searchValue},${newPaginationModel?.page},${newPaginationModel?.pageSize}`)
                    .then((res) => res.json())
                    .then((data) => {
                        filterData = data?.purchaseOrdersWithVendorsDto?.map((item: any) => {
                            return {
                                ...item,
                                dueDate: moment(new Date(item?.dueDate)).format("MM/DD/YYYY hh:mm A"),
                            };
                        })
                        setSearchTableData(filterData);
                        setTotalLength(data?.count);
                        setLoading(false);
                    })
                    .catch(error => {
                        setSearchTableData([]);
                        setTotalLength(0);
                        setLoading(false);
                        console.error(error);
                    });
                break;
            case 1:
                fetch(`${baseURL}purchaseOrders/GetSearchPurchaseOrdersByVender/${searchValue},${newPaginationModel?.page},${newPaginationModel?.pageSize}`)
                    .then((res) => res.json())
                    .then((data) => {
                        filterData = data?.purchaseOrdersWithVendorsDto?.map((item: any) => {
                            return {
                                ...item,
                                dueDate: moment(new Date(item?.dueDate)).format("MM/DD/YYYY hh:mm A"),
                            };
                        })
                        setSearchTableData(filterData);
                        setTotalLength(data?.count);
                        setLoading(false);
                    })
                    .catch(error => {
                        setSearchTableData([]);
                        setTotalLength(0);
                        setLoading(false);
                        console.error(error);
                    });
                break;
            case 2:
                fetch(`${baseURL}purchaseOrders/GetSearchPurchaseOrdersByStauts/${searchValue},${newPaginationModel?.page},${newPaginationModel?.pageSize}`)
                    .then((res) => res.json())
                    .then((data) => {
                        filterData = data?.purchaseOrdersWithVendorsDto?.map((item: any) => {
                            return {
                                ...item,
                                dueDate: moment(new Date(item?.dueDate)).format("MM/DD/YYYY hh:mm A"),
                            };
                        })
                        setSearchTableData(filterData);
                        setTotalLength(data?.count);
                        setLoading(false);
                    })
                    .catch(error => {
                        setSearchTableData([]);
                        setTotalLength(0);
                        setLoading(false);
                        console.error(error);
                    });
                break;
        }


    }

    useEffect(() => {
        if (searchValue === "" && !isFirstTime) {
            const newPaginationModel = {
                page: 0,
                pageSize: paginationModel?.pageSize,
            };

            setPaginationModel(newPaginationModel);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);

    useEffect(() => {
        if (searchValue === "") {
            setIsFirstTime(false)
            emptySearchApiCall(paginationModel);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel])


    const emptySearchApiCall = (newPaginationModel: any) => {

        setLoading(true);
        fetch(`${baseURL}purchaseOrders?page=${newPaginationModel?.page}&size=${newPaginationModel?.pageSize}`)
            .then((res) => res.json())
            .then((data) => {
                const result = data?.purchaseOrdersWithVendorsDto?.map((item: any) => {
                    return {
                        ...item,
                        dueDate: moment(new Date(item?.dueDate)).format("MM/DD/YYYY hh:mm A"),
                    };
                })

                setSearchTableData(result);
                setTotalLength(data?.count);
                setLoading(false);
            })
            .catch(error => {
                setSearchTableData([]);
                setTotalLength(0);
                setLoading(false);
                console.error(error);
            });


    }


    return (
        <div className={`px-[24px] py-[20px] h-[calc(100vh-142px)] overflow-auto`}>
            <div className="w-full h-full bg-white p-4 rounded-md">
                <div className="flex items-center justify-end py-2">
                    <div className="flex items-center gap-1.5">

                        <div>
                            <button className={`px-[8px] py-[6px] bg-primary rounded-lg border text-white flex items-center gap-1.5`}
                                onClick={() => navigate("/admin/purchase-orders/create-purchase-order")}
                            >
                                <span className="material-symbols-outlined text-[25px] font-light">
                                    add
                                </span>
                                <span className="text-xs font-medium">Create Purchase Order</span>
                            </button>
                        </div>
                    </div>
                </div>
                <div className={`w-full border border-[#E4E7EC] rounded-[16px]`}>
                    <div className={`flex justify-between items-center p-4 border-b border-[#E4E7EC]`}>
                        <div className="flex items-center gap-1.5">

                            <h2 className={`text-lg font-semibold text-[#1D2939]`}>Purchase Orders</h2>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <div >
                                <button
                                    className={`flex items-center justify-between py-[5px] px-[12px] gap-2.5 rounded-lg border border-solid`}
                                    aria-controls={open ? 'basic-menu' : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={open ? 'true' : undefined}
                                    onClick={handleClick}
                                >
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems={"center"}
                                        justifyContent={"center"}
                                    >
                                        <span className={` text-[14px] font-medium text-black-rgba`}>{selectedFilter}</span>
                                        {open ? <span className={`material-symbols-outlined  text-[25px] font-extralight`}>
                                            keyboard_arrow_up
                                        </span> : <span className={`material-symbols-outlined  text-[25px] font-extralight`}>
                                            keyboard_arrow_down
                                        </span>}
                                    </Stack>
                                </button>

                                <Menu
                                    id="basic-menu"
                                    anchorEl={anchorEl}
                                    open={open}
                                    onClose={handleClose}
                                    slotProps={{
                                        paper: {
                                            sx: {
                                                width: 170,
                                            }
                                        }
                                    }}
                                >
                                    {
                                        filterItems?.map((filter, index) => (
                                            <>
                                                <MenuItem
                                                    key={index}
                                                    sx={{ fontSize: "12px", marginBlock: "0px" }}
                                                    onClick={() => {
                                                        setSelectedFilter(filter.label)
                                                        setFilterValue(filter.id)
                                                        setAnchorEl(null);

                                                    }}>
                                                    <span className={`text-[16px] font-medium text-black-rgba`}>{filter.label}</span>
                                                </MenuItem>

                                            </>

                                        ))
                                    }
                                </Menu>
                            </div>
                            <div className={`flex px-[5px] py-[6px] items-center gap-2.5 rounded-lg border shadow-sm`}>
                                <span className={`material-symbols-outlined text-[25px] font-light text-black-rgba`}>
                                    search
                                </span>
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    className={`border-none outline-none w-full bg-transparent`}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <CustomDataGrid
                        filteredRows={searchTableData}
                        columns={columns?.map(col => ({
                            ...col,
                            headerClassName: "bg-[#F9FAFB]",
                        }))}
                        loading={loading}
                        totalLength={totalLength}
                        paginationModel={paginationModel}
                        setPaginationModel={setPaginationModel}
                    />
                </div>
            </div>

        </div>
    )
}